// src/hooks/auth/useAuth.ts
import { useState, useEffect, useCallback } from "react";
import { supabase } from "../../lib/supabase";
import { authInterceptor } from "../../services/Authentication/authInterceptor";
import { secureStorage } from "../../services/Storage/secureStorage";
import { config } from "../../config/env";
import { LogoutManager } from "../../utils/Authentication/logoutUtils";
import { sessionManager } from "../../services/Authentication/sessionManager";
import { handleAuthError } from "../../utils/Authentication/authErrorHandler";
import { logger } from "../../utils/logger";

export const useAuth = () => {
  // Check if dev bypass mode is enabled
  const isDevBypass = config.dev.bypassAuth;

  // In mock credentials mode, try to initialize from stored data
  const initializeFromStorage = () => {
    if (config.dev.mockCredentials) {
      const token = authInterceptor.getAccessToken();
      const storedUser = secureStorage.getUserData();
      
      if (token && storedUser) {
        const tokenParts = token.split('.');
        if (tokenParts.length === 3) {
          try {
            const payload = JSON.parse(atob(tokenParts[1]));
            if (payload.sub && payload.email) {
              return { user: storedUser, isAuthenticated: true, loading: false };
            }
          } catch (e) {
            // Not a valid JWT format
          }
        }
      }
    }
    return { user: null, isAuthenticated: false, loading: true };
  };
  
  // Dev bypass always wins over storage/mock-credential init, but every hook
  // below still runs unconditionally on every render (rules-of-hooks) —
  // behavior is gated by isDevBypass inside each hook body instead.
  const initialAuth = isDevBypass
    ? { user: config.dev.mockUser, isAuthenticated: true, loading: false }
    : initializeFromStorage();
  const [user, setUser] = useState<any>(initialAuth.user);
  const [loading, setLoading] = useState(initialAuth.loading);
  const [isAuthenticated, setIsAuthenticated] = useState(initialAuth.isAuthenticated);

  const checkAuth = useCallback(async () => {
    if (isDevBypass) {
      console.log('Dev mode: Auth check bypassed');
      return;
    }

    // Skip auth check if already authenticated via social login
    if (isAuthenticated && user) {
      logger.log('Skipping auth check - user already authenticated via social login');
      return;
    }

    // Mock credentials mode - check for stored mock user data and tokens
    if (config.dev.mockCredentials) {
      setLoading(true);
      const token = authInterceptor.getAccessToken();
      const storedUser = secureStorage.getUserData();
      
      if (token && storedUser) {
        // Verify token is a mock token (JWT-like format with 3 parts)
        const tokenParts = token.split('.');
        if (tokenParts.length === 3) {
          try {
            // Parse the payload (second part) of the JWT
            const payload = JSON.parse(atob(tokenParts[1]));
            if (payload.sub && payload.email) {
              // Valid mock JWT token
              setUser(storedUser);
              setIsAuthenticated(true);
              setLoading(false);
              console.log('🔓 Mock credentials mode: User authenticated from stored data');
              return;
            }
          } catch (e) {
            // Not a valid JWT format, continue to check other formats
          }
        }
        
        // Check if token starts with mock- (alternative format)
        if (token.startsWith('mock-')) {
          setUser(storedUser);
          setIsAuthenticated(true);
          setLoading(false);
          console.log('🔓 Mock credentials mode: User authenticated from stored data');
          return;
        }
      }
      
      // No valid mock tokens found
      setIsAuthenticated(false);
      setUser(null);
      setLoading(false);
      return;
    }

    try {
      // Re-read on every attempt below: a refresh replaces this token, and
      // retrying with the captured one guarantees the same 401.
      let token = authInterceptor.getAccessToken();
      if (!token) {
        setIsAuthenticated(false);
        setUser(null);
        setLoading(false);
        return;
      }

      // Enhanced token validation with retry logic
      let response;
      let retryCount = 0;
      const maxRetries = 2;

      while (retryCount <= maxRetries) {
        try {
          response = await fetch(`${config.api.baseUrl}/auth/me`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          if (response.ok) {
            break; // Success, exit retry loop
          } else if (response.status === 401 && retryCount < maxRetries) {
            // The server rejected this token, so force a real refresh — an
            // unexpired token is not necessarily an accepted one, and the
            // unforced path would just hand the same rejected token back.
            logger.log(`Token validation failed (attempt ${retryCount + 1}), attempting refresh...`);
            try {
              const refreshed = await authInterceptor.performRefresh(true);
              if (!refreshed) {
                // Nothing left to try — the refresh token is dead too.
                logger.log('Refresh produced no session; treating as signed out.');
                break;
              }
              // Retry with the NEW token. Reusing the old one made the retry
              // loop pure ceremony: three identical requests, three 401s.
              token = authInterceptor.getAccessToken() ?? token;
              retryCount++;
              continue;
            } catch (refreshError) {
              console.error("Token refresh failed:", refreshError);
              break;
            }
          } else {
            break; // Other error or max retries reached
          }
        } catch (fetchError) {
          console.error(`Fetch error (attempt ${retryCount + 1}):`, fetchError);
          if (retryCount >= maxRetries) {
            throw fetchError;
          }
          retryCount++;
          // Wait before retry
          const currentRetryCount = retryCount;
          await new Promise(resolve => setTimeout(resolve, 1000 * currentRetryCount));
        }
      }

      if (response && response.ok) {
        const data = await response.json();
        setUser(data.data.user);
        setIsAuthenticated(true);
        secureStorage.saveUserData(data.data.user);
        
        // Validate session if user is authenticated and has valid tokens
        const accessToken = authInterceptor.getAccessToken();
        const refreshToken = authInterceptor.getRefreshToken();
        
        // Check if this is a social login (Facebook/Google) by looking at the session data
        const isSocialLogin = data.data.user?.app_metadata?.provider !== 'email' || 
                             data.data.user?.user_metadata?.provider !== 'email';
        
        // Only attempt session creation if we have valid tokens AND user is not already authenticated via social login
        if (data.data.user && accessToken && refreshToken && 
            accessToken.length > 100 && refreshToken.length > 10 && 
            !isAuthenticated && !isSocialLogin) { // Don't create session if already authenticated or social login
          try {
            await sessionManager.createSession(data.data.user.id, 'email');
          } catch (sessionError) {
            console.warn("Session creation failed - user may not be fully authenticated yet:", sessionError);
            // Don't fail authentication if session creation fails - this is normal during email confirmation
          }
        } else if (isAuthenticated) {
          // Debug logging removed for cleaner console
        } else if (isSocialLogin) {
          // Debug logging removed for cleaner console
        } else {
          // Debug logging removed for cleaner console
          // Clear invalid tokens
          if (accessToken && accessToken.length <= 100) {
            if (process.env.NODE_ENV === 'development') {
              logger.log('Clearing invalid access token');
            }
            authInterceptor.clearTokens();
          }
        }
      } else {
        setIsAuthenticated(false);
        setUser(null);
        secureStorage.clearTokens();
      }
    } catch (error) {
      console.error("Auth check failed:", error);
      
      // Use enhanced error handling
      const authError = handleAuthError(error, "auth_check");
      
      setIsAuthenticated(false);
      setUser(null);
      secureStorage.clearTokens();
      
      // Check if we should redirect to login
      if (authError.code === 'TOKEN_EXPIRED' || authError.code === 'TOKEN_INVALID' || authError.code === 'REFRESH_FAILED' || authError.code === 'UNAUTHORIZED') {
        LogoutManager.silentLogout();
      }
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user, isDevBypass]);

  const login = useCallback(async (email: string, password: string): Promise<{
    success: boolean;
    error?: string;
    message?: string;
    requiresEmailVerification?: boolean;
    data?: any;
    remainingAttempts?: number;
    lockoutExpiresAt?: Date;
    lockoutLevel?: number;
    timeUntilUnlock?: number;
    shouldRedirectToAdmin?: boolean;
    userRole?: string;
  }> => {
    if (isDevBypass) {
      return { success: true, message: 'Dev mode: Login bypassed' };
    }

    // Mock credentials mode - accept any email/password combination
    if (config.dev.mockCredentials) {
      const mockUser = {
        id: `mock-user-${email.replace(/[^a-zA-Z0-9]/g, '')}`,
        email: email,
        user_metadata: {
          role: 'user',
          persona: 'adventurer',
          display_name: email.split('@')[0] || 'Demo User',
        },
        app_metadata: {
          provider: 'email',
        },
        created_at: new Date().toISOString(),
      };
      
      // Create mock tokens (simple base64 encoded strings)
      const mockAccessToken = btoa(JSON.stringify({ 
        sub: mockUser.id, 
        email: email,
        exp: Math.floor(Date.now() / 1000) + 3600 
      }));
      const mockRefreshToken = `mock-refresh-${Date.now()}`;
      
      // Store mock tokens
      authInterceptor.saveTokens(mockAccessToken, mockRefreshToken);
      setUser(mockUser);
      setIsAuthenticated(true);
      secureStorage.saveUserData(mockUser);
      
      console.log('🔓 Mock credentials mode: Login successful with any credentials');
      
      return { 
        success: true,
        userRole: 'user'
      };
    }
    
    try {
      const response = await fetch(`${config.api.baseUrl}/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${config.supabase.anonKey}`,
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();
      if (data.success) {
        // Debug logging removed for cleaner console
        
        // Check if session exists (user might not be confirmed yet)
        if (data.data.session && data.data.session.access_token && data.data.session.refresh_token) {
          // Validate tokens before storing them
          const accessToken = data.data.session.access_token;
          const refreshToken = data.data.session.refresh_token;
          
          // Basic JWT validation - access token must be JWT, refresh token can be shorter for unconfirmed users
          if (accessToken.split('.').length === 3) {
            authInterceptor.saveTokens(accessToken, refreshToken);
            setUser(data.data.user);
            setIsAuthenticated(true);
            secureStorage.saveUserData(data.data.user);
            
            // Debug logging removed for cleaner console
            
            // Create session for the user only if they have valid tokens
            try {
              await sessionManager.createSession(data.data.user.id, 'email');
            } catch (sessionError) {
              console.warn("Session creation failed during login:", sessionError);
              // Don't fail login if session creation fails - this is normal during email confirmation
            }
          } else {
            console.warn('Invalid access token received from login - not storing tokens');
            console.warn('Access token parts:', accessToken.split('.').length);
            console.warn('Access token preview:', accessToken.substring(0, 20) + '...');
            setUser(data.data.user);
            setIsAuthenticated(false); // Don't mark as authenticated without valid tokens
          }
        } else {
          // User exists but no session - likely email not confirmed
          logger.log('Login successful but no session - user may need to confirm email');
          setUser(data.data.user);
          setIsAuthenticated(false); // Don't mark as authenticated without session
          
          // Return a specific message for unverified users
          return { 
            success: true, 
            data: data.data,
            message: 'Please check your email and click the verification link to complete your login.',
            requiresEmailVerification: true
          };
        }
        
        // Check user role and return appropriate redirect info
        const userRole = data.data.user?.user_metadata?.role || 'user';
        const shouldRedirectToAdmin = userRole === 'admin';
        
        return { 
          success: true,
          shouldRedirectToAdmin,
          userRole
        };
      }
      
      // Handle account lockout and invalid credentials with specific details
      if (data.error === 'ACCOUNT_LOCKED') {
        return {
          success: false,
          error: 'ACCOUNT_LOCKED',
          message: data.message,
          lockoutLevel: data.lockoutLevel,
          lockoutExpiresAt: data.lockoutExpiresAt ? new Date(data.lockoutExpiresAt) : undefined,
          timeUntilUnlock: data.timeUntilUnlock
        };
      }
      
      if (data.error === 'INVALID_CREDENTIALS') {
        return {
          success: false,
          error: 'INVALID_CREDENTIALS',
          message: data.message,
          remainingAttempts: data.remainingAttempts,
          lockoutLevel: data.lockoutLevel,
          lockoutExpiresAt: data.lockoutExpiresAt ? new Date(data.lockoutExpiresAt) : undefined,
          timeUntilUnlock: data.timeUntilUnlock
        };
      }
      
      return { success: false, error: data.message };
    } catch (_error) {
      return { success: false, error: "Login failed" };
    }
  }, [isDevBypass]);

  const logout = useCallback(async () => {
    if (isDevBypass) {
      console.log('Dev mode: Logout bypassed');
      return;
    }

    // Clean up session manager
    sessionManager.cleanup();

    await LogoutManager.logout({
      showMessage: false,
      reason: 'user_initiated'
    });
  }, [isDevBypass]);

  const socialLogin = useCallback(async (provider: 'google' | 'facebook') => {
    if (isDevBypass) {
      return { success: true, message: 'Dev mode: Social login bypassed' };
    }

    try {
      // This would integrate with your existing social login flow
      // For now, we'll use Supabase's built-in OAuth
      // Note: Social login redirect will be handled by the role-based navigation hook
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: `${window.location.origin}/dashboard` // Default redirect, role-based navigation will handle the actual routing
        }
      });

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (_error) {
      return { success: false, error: "Social login failed" };
    }
  }, [isDevBypass]);

  // Handle social authentication data population
  const handleSocialAuthData = async (session: any) => {
    try {
      // Prevent multiple executions for the same session
      const sessionKey = `social_auth_processed_${session.user.id}_${session.expires_at}`;
      if (localStorage.getItem(sessionKey)) {
        logger.log('Social auth data already processed for this session, skipping...');
        return;
      }
      
      // Clean up old session flags (keep only current session)
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('social_auth_processed_') && key !== sessionKey) {
          localStorage.removeItem(key);
        }
      });
      
      logger.log('handleSocialAuthData called with session:', session);
      const user = session.user;
      const userMetadata = user.user_metadata || {};
      const appMetadata = user.app_metadata || {};
      
      logger.log('User metadata:', userMetadata);
      logger.log('App metadata:', appMetadata);
      
      // Check if this is a social login by looking at the provider
      // For Supabase social auth, provider info is in different locations
      let provider = appMetadata.provider || userMetadata.provider;
      
      // Override provider detection for social logins - check Google first
      if (userMetadata.iss?.includes('accounts.google.com') || userMetadata.iss?.includes('google')) {
        provider = 'google'; // Google has iss in metadata
      } else if (session.provider_token && userMetadata.iss?.includes('facebook')) {
        provider = 'facebook'; // Facebook has provider_token AND facebook iss
      } else if (session.provider_token) {
        // If we have provider_token but no clear Google/Facebook indicator, 
        // check the provider_token format or other metadata
        if (userMetadata.iss?.includes('graph.facebook.com')) {
          provider = 'facebook';
        } else {
          // Default to facebook if we have provider_token but can't determine
          provider = 'facebook';
        }
      }
      
      const providerId = userMetadata.sub || userMetadata.provider_id || 
                        userMetadata.identities?.[0]?.identity_id || // Supabase stores provider ID here
                        userMetadata.identities?.[0]?.id;
      
      logger.log('Provider detection details:', {
        appMetadata_provider: appMetadata.provider,
        userMetadata_provider: userMetadata.provider,
        userMetadata_iss: userMetadata.iss,
        session_provider_token_exists: !!session.provider_token,
        final_provider: provider
      });
      logger.log('Detected provider:', provider);
      logger.log('Provider ID:', providerId);
      logger.log('Session provider_token exists:', !!session.provider_token);
      logger.log('User identities:', userMetadata.identities);
      
      if (provider && (provider === 'google' || provider === 'facebook')) {
        logger.log(`Processing ${provider} login data for user:`, user.id);
        
        // 1. Create/update user_social_accounts record
        const socialAccountData = {
          user_id: user.id,
          provider: provider,
          provider_id: providerId,
          provider_email: user.email,
          provider_name: userMetadata.full_name || userMetadata.name || user.user_metadata?.name,
          provider_picture_url: userMetadata.avatar_url || userMetadata.picture,
          is_primary: true
        };
        
        logger.log('Social account data:', socialAccountData);
        
        // Check if social account already exists
        const { data: existingSocialAccount } = await supabase
          .from('user_social_accounts')
          .select('*')
          .eq('user_id', user.id)
          .eq('provider', provider)
          .maybeSingle(); // Use maybeSingle() instead of single() to handle no results gracefully
        
        if (!existingSocialAccount) {
          const { error: socialError } = await supabase
            .from('user_social_accounts')
            .insert(socialAccountData);
          
          if (socialError) {
            console.error('Error creating social account:', socialError);
          } else {
            logger.log(`Successfully created ${provider} social account`);
          }
        } else {
          logger.log(`${provider} social account already exists`);
        }
        
        // 2. Create user_sessions record
        const sessionData = {
          user_id: user.id,
          session_token: session.access_token?.substring(0, 255) || null, // Truncate if too long
          refresh_token_hash: session.refresh_token?.substring(0, 255) || null, // Truncate if too long
          device_name: navigator.userAgent.includes('Chrome') ? 'Chrome' : 
                      navigator.userAgent.includes('Firefox') ? 'Firefox' : 
                      navigator.userAgent.includes('Safari') ? 'Safari' : 'Unknown',
          device_type: /Mobile|Android|iPhone|iPad/.test(navigator.userAgent) ? 'mobile' : 'desktop',
          browser_name: navigator.userAgent.includes('Chrome') ? 'Chrome' : 
                       navigator.userAgent.includes('Firefox') ? 'Firefox' : 
                       navigator.userAgent.includes('Safari') ? 'Safari' : 'Unknown',
          browser_version: navigator.userAgent.match(/(Chrome|Firefox|Safari)\/([0-9.]+)/)?.[2] || 'Unknown',
          os_name: navigator.userAgent.includes('Windows') ? 'Windows' : 
                  navigator.userAgent.includes('Mac') ? 'macOS' : 
                  navigator.userAgent.includes('Linux') ? 'Linux' : 
                  navigator.userAgent.includes('Android') ? 'Android' : 
                  navigator.userAgent.includes('iOS') ? 'iOS' : 'Unknown',
          os_version: null, // Would need additional parsing
          ip_address: null, // Would need to get from backend
          country: null, // Would need geolocation
          city: null, // Would need geolocation
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          login_method: provider,
          expires_at: session.expires_at ? new Date(session.expires_at * 1000).toISOString() : 
                     new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), // 7 days default
          is_active: true
        };
        
        logger.log('Session data lengths:', {
          session_token_length: sessionData.session_token?.length || 0,
          refresh_token_hash_length: sessionData.refresh_token_hash?.length || 0,
          device_name_length: sessionData.device_name?.length || 0,
          browser_name_length: sessionData.browser_name?.length || 0,
          os_name_length: sessionData.os_name?.length || 0,
          timezone_length: sessionData.timezone?.length || 0
        });
        logger.log('Session data:', { ...sessionData, session_token: '***', refresh_token_hash: '***' });
        
        // Check if session already exists to avoid duplicate key error
        const { data: existingSession } = await supabase
          .from('user_sessions')
          .select('id')
          .eq('user_id', user.id)
          .eq('session_token', sessionData.session_token)
          .maybeSingle();
        
        if (!existingSession) {
          const { error: sessionError } = await supabase
            .from('user_sessions')
            .insert(sessionData);
          
          if (sessionError) {
            console.error('Error creating session:', sessionError);
          } else {
            logger.log(`Successfully created ${provider} session`);
          }
        } else {
          logger.log(`${provider} session already exists, skipping creation`);
        }
        
        // 3. Log the social login event
        const { error: logError } = await supabase
          .from('system_logs')
          .insert({
            log_level: 'info',
            message: `Social login successful: ${user.email} via ${provider}`,
            context: {
              event_type: 'social_login',
              email: user.email,
              userId: user.id,
              provider: provider,
              provider_id: providerId,
              timestamp: new Date().toISOString()
            }
          });
        
        if (logError) {
          console.error('Error logging social login:', logError);
        }
        
      } else {
        logger.log('Email login detected, creating session record only');
        
        // For email logins, still create a session record
        const sessionData = {
          user_id: user.id,
          session_token: session.access_token?.substring(0, 255) || null, // Truncate if too long
          refresh_token_hash: session.refresh_token?.substring(0, 255) || null, // Truncate if too long
          device_name: navigator.userAgent.includes('Chrome') ? 'Chrome' : 
                      navigator.userAgent.includes('Firefox') ? 'Firefox' : 
                      navigator.userAgent.includes('Safari') ? 'Safari' : 'Unknown',
          device_type: /Mobile|Android|iPhone|iPad/.test(navigator.userAgent) ? 'mobile' : 'desktop',
          browser_name: navigator.userAgent.includes('Chrome') ? 'Chrome' : 
                       navigator.userAgent.includes('Firefox') ? 'Firefox' : 
                       navigator.userAgent.includes('Safari') ? 'Safari' : 'Unknown',
          browser_version: navigator.userAgent.match(/(Chrome|Firefox|Safari)\/([0-9.]+)/)?.[2] || 'Unknown',
          os_name: navigator.userAgent.includes('Windows') ? 'Windows' : 
                  navigator.userAgent.includes('Mac') ? 'macOS' : 
                  navigator.userAgent.includes('Linux') ? 'Linux' : 
                  navigator.userAgent.includes('Android') ? 'Android' : 
                  navigator.userAgent.includes('iOS') ? 'iOS' : 'Unknown',
          os_version: null, // Would need additional parsing
          ip_address: null, // Would need to get from backend
          country: null, // Would need geolocation
          city: null, // Would need geolocation
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          login_method: 'email',
          expires_at: session.expires_at ? new Date(session.expires_at * 1000).toISOString() : 
                     new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), // 7 days default
          is_active: true
        };
        
        logger.log('Email session data lengths:', {
          session_token_length: sessionData.session_token?.length || 0,
          refresh_token_hash_length: sessionData.refresh_token_hash?.length || 0,
          device_name_length: sessionData.device_name?.length || 0,
          browser_name_length: sessionData.browser_name?.length || 0,
          os_name_length: sessionData.os_name?.length || 0,
          timezone_length: sessionData.timezone?.length || 0
        });
        logger.log('Email session data:', { ...sessionData, session_token: '***', refresh_token_hash: '***' });
        
        // Check if session already exists to avoid duplicate key error
        const { data: existingEmailSession } = await supabase
          .from('user_sessions')
          .select('id')
          .eq('user_id', user.id)
          .eq('session_token', sessionData.session_token)
          .maybeSingle();
        
        if (!existingEmailSession) {
          const { error: sessionError } = await supabase
            .from('user_sessions')
            .insert(sessionData);
          
          if (sessionError) {
            console.error('Error creating email session:', sessionError);
          } else {
            logger.log('Successfully created email session');
          }
        } else {
          logger.log('Email session already exists, skipping creation');
        }
        
        // Log the email login event
        const { error: logError } = await supabase
          .from('system_logs')
          .insert({
            log_level: 'info',
            message: `Email login successful: ${user.email}`,
            context: {
              event_type: 'login_success',
              email: user.email,
              userId: user.id,
              login_method: 'email',
              timestamp: new Date().toISOString()
            }
          });
        
        if (logError) {
          console.error('Error logging email login:', logError);
        }
      }
      
      // Mark this session as processed to prevent duplicate processing
      localStorage.setItem(sessionKey, 'true');
      logger.log('Social auth data processing completed and marked as processed');
      
    } catch (error) {
      console.error('Error handling social auth data:', error);
    }
  };

  // Handle email confirmation
  useEffect(() => {
    if (isDevBypass) return;

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        // Suppress INITIAL_SESSION logs - only log meaningful state changes
        if (process.env.NODE_ENV === 'development' && event !== 'INITIAL_SESSION') {
          logger.log("Auth state change:", event, session);
        }
        
        if (event === "SIGNED_IN" && session) {
          if (process.env.NODE_ENV === 'development') {
            logger.log('SIGNED_IN event detected, processing...');
          }
          
          // Check if this is a legitimate sign-in (not a session restoration after logout)
          const isLogoutInProgress = localStorage.getItem('logout_in_progress');
          
          if (isLogoutInProgress) {
            logger.log("Ignoring SIGNED_IN event during logout process");
            // Clear the flag after a short delay to prevent blocking legitimate logins
            setTimeout(() => {
              localStorage.removeItem('logout_in_progress');
              logger.log('Cleared logout flag after delay');
            }, 1000);
            return;
          }
          
          logger.log('Processing legitimate SIGNED_IN event...');
          
          // User confirmed email and signed in
          logger.log("User confirmed email and signed in:", session.user);
          logger.log("Session expires at:", session.expires_at ? new Date(session.expires_at * 1000) : 'Not available');
          logger.log("Current time:", new Date());
          setUser(session.user);
          setIsAuthenticated(true);
          setLoading(false); // Set loading to false immediately when user signs in
          
          // Sync Supabase tokens to our custom storage system
          try {
            if (session.access_token && session.refresh_token) {
              logger.log("Syncing Supabase tokens to custom storage...");
              secureStorage.saveTokens(session.access_token, session.refresh_token);
              secureStorage.saveUserData(session.user);
              logger.log("Tokens synced successfully");
            }
          } catch (error) {
            console.error("Failed to sync tokens:", error);
          }
          
          // Update email verification status in database (optional - don't block if it fails)
          try {
            const { error: updateError } = await supabase
              .from("users")
              .update({ email_verified: true })
              .eq("id", session.user.id);
            
            if (updateError) {
              console.warn("Email verification status update failed (this is OK for social logins):", updateError.message);
            } else {
              logger.log('Email verification status updated successfully');
            }
          } catch (error) {
            console.warn("Email verification status update failed (this is OK for social logins):", error);
          }

          logger.log('Continuing to handleSocialAuthData...');

          // Handle authentication data population (social and email)
          logger.log('About to call handleSocialAuthData...');
          try {
            await handleSocialAuthData(session);
            logger.log('handleSocialAuthData completed successfully');
          } catch (error) {
            console.error('Error in handleSocialAuthData:', error);
          }
          
          // Don't force navigation - let the app handle it naturally
          logger.log('Authentication data processing completed, app will handle navigation');
        } else if (event === "TOKEN_REFRESHED" && session) {
          // Supabase refreshed tokens - sync to our custom storage
          if (process.env.NODE_ENV === 'development') {
            logger.log("Supabase tokens refreshed, syncing to custom storage");
          }
          
          try {
            if (session.access_token && session.refresh_token) {
              secureStorage.saveTokens(session.access_token, session.refresh_token);
              secureStorage.saveUserData(session.user);
              
              // Always update state from session (source of truth)
              setUser(session.user);
              setIsAuthenticated(true);
            }
          } catch (error) {
            console.error("Failed to sync refreshed tokens:", error);
          }
        } else if (event === "SIGNED_OUT") {
          logger.log("User signed out");
          setUser(null);
          setIsAuthenticated(false);
          
          // Clear custom storage when Supabase signs out
          try {
            logger.log("Clearing custom storage on sign out...");
            secureStorage.clearTokens();
            logger.log("Custom storage cleared");
          } catch (error) {
            console.error("Failed to clear custom storage:", error);
          }
        }
      }
    );

    return () => subscription.unsubscribe();
  }, [isDevBypass]);

  useEffect(() => {
    if (isDevBypass) return;

    // Clear any stuck logout flags on app initialization
    const logoutFlag = localStorage.getItem('logout_in_progress');
    if (logoutFlag) {
      logger.log('Clearing stuck logout flag on app initialization');
      localStorage.removeItem('logout_in_progress');
    }

    checkAuth();
  }, [isDevBypass]); // Only run once on mount

  // Enhanced token monitoring
  useEffect(() => {
    if (isDevBypass || !isAuthenticated) return;

    // Periodic token validation
    const interval = setInterval(async () => {
      try {
        await checkAuth(); // Re-validate token every 5 minutes
      } catch (error) {
        console.error("Token validation failed:", error);
        // Token is invalid, will be handled by authInterceptor
      }
    }, 5 * 60 * 1000); // Every 5 minutes

    // Monitor for token expiration on visibility change
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        // Re-validate token when user returns to tab
        checkAuth();
      }
    };

    // Monitor for token expiration on focus
    const handleFocus = () => {
      checkAuth();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);

    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
    };
  }, [isAuthenticated, isDevBypass]); // Remove checkAuth from dependencies to prevent infinite loops

  // Session management methods
  const getUserSessions = useCallback(async () => {
    if (isDevBypass) return [];
    return await sessionManager.getUserSessions();
  }, [isDevBypass]);

  const revokeSession = useCallback(async (sessionId: string) => {
    if (isDevBypass) return true;
    return await sessionManager.revokeSession(sessionId);
  }, [isDevBypass]);

  const revokeAllOtherSessions = useCallback(async () => {
    if (isDevBypass) return true;
    return await sessionManager.revokeAllOtherSessions();
  }, [isDevBypass]);

  const getCurrentSessionId = useCallback(() => {
    if (isDevBypass) return 'dev-session-123';
    return sessionManager.getCurrentSessionId();
  }, [isDevBypass]);

  return {
    user,
    loading,
    isAuthenticated,
    login,
    logout,
    socialLogin,
    checkAuth,
    // Session management
    getUserSessions,
    revokeSession,
    revokeAllOtherSessions,
    getCurrentSessionId,
  };
};
